import { useEffect, useState } from "react";
import { Common, Icon } from "@/components";
import { useImportOrder } from "../providers/ImportOrderContext";
import {
  useUploadFile,
  useBasicValidate,
  useAdvancedValidate,
} from "../services/salesOrdersService";
import { useNavigate } from "react-router-dom";

enum ImportStep {
  UPLOAD_FILE = "upload_file",
  BASIC_VALIDATE = "basic_validate",
  ADVANCED_VALIDATE = "advanced_validate",
}

const UploadProgress = () => {
  const { state, dispatch, handleCancel, refetchMappings } = useImportOrder();
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<ImportStep>(
    ImportStep.UPLOAD_FILE
  );
  const uploadFileMutation = useUploadFile();
  const basicValidateMutation = useBasicValidate();
  const advancedValidateMutation = useAdvancedValidate();
  const navigate = useNavigate();

  useEffect(() => {
    const processImport = async () => {
      try {
        // Step 1: Upload File
        setCurrentStep(ImportStep.UPLOAD_FILE);
        const formData = new FormData();
        formData.append("file", state.selectedFile as File);
        formData.append("importMethod", state.importMethod);
        if (state.documentType) {
          formData.append("documentType", state.documentType);
        }
        if (state.taxType) {
          formData.append("taxType", state.taxType);
        }
        if (state.addressMode) {
          formData.append("addressMode", state.addressMode);
        }
        formData.append("mappingFields", JSON.stringify(state.mappingFields));

        const uploadResult = await uploadFileMutation.mutateAsync(formData);
        if (!uploadResult.success) {
          // If upload fails, move to Result screen with errors
          dispatch({
            type: "SET_IMPORT_RESULT",
            payload: {
              success: false,
              recordsImported: 0,
              errors: uploadResult.errors ?? [],
            },
          });
          navigate("#result");
          setIsLoading(false);
          return;
        }

        // Store import session ID
        const importSessionId = uploadResult.importSessionId ?? "";
        dispatch({ type: "SET_SESSION_ID", payload: importSessionId });

        // Step 2: Basic Validation
        setCurrentStep(ImportStep.BASIC_VALIDATE);
        const basicValidateResult = await basicValidateMutation.mutateAsync(
          importSessionId
        );
        if (!basicValidateResult.success) {
          // If basic validation fails, move to Result screen with errors
          dispatch({
            type: "SET_IMPORT_RESULT",
            payload: {
              success: false,
              recordsImported: 0,
              errors: basicValidateResult.errors,
              errorDetails: basicValidateResult.errorDetails,
            },
          });
          navigate("#result");
          setIsLoading(false);
          return;
        }

        // Step 3: Advanced Validation
        setCurrentStep(ImportStep.ADVANCED_VALIDATE);
        const advancedValidateResult =
          await advancedValidateMutation.mutateAsync(importSessionId);

        if (
          !advancedValidateResult.success &&
          advancedValidateResult.errors.length > 0
        ) {
          // If advanced validation fails with errors, move to Result screen
          dispatch({
            type: "SET_IMPORT_RESULT",
            payload: {
              success: false,
              recordsImported: 0,
              errors: advancedValidateResult.errors,
            },
          });
          navigate("#result");
          setIsLoading(false);
          return;
        }

        dispatch({
          type: "SET_RECORD_COUNT",
          payload: advancedValidateResult.recordCount ?? 0,
        });

        const invalidAddressesDocNumbers = (
          advancedValidateResult.invalidAddresses ?? []
        )
          .map((invalidAddress) => invalidAddress.docNumbers)
          .flat();

        const invalidRoutesDocNumbers = (
          advancedValidateResult.invalidRoutes ?? []
        )
          .map((invalidRoute) => invalidRoute.docNumbers)
          .flat();

        const invalidDocNumbers = Array.from(
          new Set([...invalidAddressesDocNumbers, ...invalidRoutesDocNumbers])
        );

        dispatch({
          type: "SET_INVALID_ORDER_COUNT",
          payload: invalidDocNumbers.length ?? 0,
        });

        dispatch({
          type: "SET_INVALID_ADDRESSES",
          payload: advancedValidateResult.invalidAddresses ?? [],
        });

        dispatch({
          type: "SET_INVALID_ROUTES",
          payload: advancedValidateResult.invalidRoutes ?? [],
        });

        // If all validations pass, go to Review screen
        navigate("#review");
        setIsLoading(false);
      } catch (error) {
        console.error("Import process failed:", error);
        dispatch({
          type: "SET_IMPORT_RESULT",
          payload: {
            success: false,
            recordsImported: 0,
            errors: ["An unexpected error occurred during the import process."],
          },
        });
        navigate("#result");
        setIsLoading(false);
      }
    };

    processImport().then(() => {
      refetchMappings();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProgressMessage = () => {
    switch (currentStep) {
      case ImportStep.UPLOAD_FILE:
        return "Uploading your data...";
      case ImportStep.BASIC_VALIDATE:
      case ImportStep.ADVANCED_VALIDATE:
        return "Validating your data...";
      default:
        return "Processing...";
    }
  };

  const getProgressDescription = () => {
    switch (currentStep) {
      case ImportStep.UPLOAD_FILE:
        return "Uploading your file to the server.";
      case ImportStep.BASIC_VALIDATE:
      case ImportStep.ADVANCED_VALIDATE:
        return "Checking your data for validation issues.";
      default:
        return "";
    }
  };

  return (
    <div className="relative">
      <div className="h-[calc(100vh-258px)] flex items-center justify-center flex-col">
        {isLoading && (
          <Common.NoData
            iconRender={Icon.Loading}
            title={getProgressMessage()}
            description={getProgressDescription()}
          />
        )}
      </div>

      <div className="flex justify-end space-x-3 mt-5">
        <Common.Button
          color="transparent"
          onClick={() => handleCancel({ mode: "cancel", needConfirm: true })}
          disabled={isLoading}
        >
          Cancel
        </Common.Button>
      </div>
    </div>
  );
};

export default UploadProgress;
